import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { LOCALE_CODE_MAX, isValidCommunicationLocaleCode } from "../shared/communications-types";
import { resolveCommunicationError } from "../shared/resolve-communication-error";
import { useCreateCommunicationLocale } from "./use-communication-locales";

interface Props {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
}

export function CreateCommunicationLocaleDialog({ open, onOpenChange }: Props) {
  const { t } = useTranslation();
  const create = useCreateCommunicationLocale();

  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setCode("");
      setError(null);
    }
  }, [open]);

  const trimmedCode = code.trim();
  // The API accepts the code verbatim (it only derives `language` from the
  // primary subtag) — we don't canonicalise so script/region codes like
  // `es-419` / `zh-Hant-TW` aren't mangled.
  const isValid = isValidCommunicationLocaleCode(trimmedCode);

  const handleSubmit = async () => {
    if (!isValid || create.isPending) return;
    setError(null);
    try {
      await create.mutateAsync({ code: trimmedCode });
      onOpenChange(false);
    } catch (err) {
      setError(resolveCommunicationError(err, t));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !create.isPending && onOpenChange(next)}>
      <DialogContent data-testid="communication-create-locale-dialog">
        <DialogHeader>
          <DialogTitle>{t("communications.locales.create.title")}</DialogTitle>
          <DialogDescription>{t("communications.locales.create.description")}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="comm-locale-code">{t("communications.locales.fields.code")}</Label>
            <Input
              id="comm-locale-code"
              data-testid="communication-locale-code-input"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              maxLength={LOCALE_CODE_MAX}
              placeholder="es-AR"
              autoFocus
            />
            <p className="text-xs text-muted-foreground">
              {t("communications.locales.fields.codeHint")}
            </p>
          </div>

          {error && (
            <p data-testid="communication-create-locale-error" className="text-sm text-destructive">
              {error}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={create.isPending}>
            {t("communications.actions.cancel")}
          </Button>
          <Button
            data-testid="communication-create-locale-submit"
            onClick={handleSubmit}
            disabled={!isValid || create.isPending}
          >
            {create.isPending
              ? t("communications.actions.saving")
              : t("communications.actions.create")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
